import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigationType } from 'react-router-dom'

export interface PageHeaderProps {
  hidden: boolean
  theme: 'primary' | 'secondary'
  background: boolean
  hideLinks: boolean
}

const defaultHeaderProps: PageHeaderProps = {
  hidden: false,
  theme: 'primary',
  background: true,
  hideLinks: false,
}

export interface PageContextProps {
  hideFooter: boolean
  setHideFooter: Dispatch<SetStateAction<boolean>>
  header: PageHeaderProps
  setHeader: Dispatch<SetStateAction<PageHeaderProps>>
}

export const PageContext = createContext({} as PageContextProps)

interface PageProviderProps {
  children?: React.ReactNode | string | null
}

export const PageProvider = ({ children }: PageProviderProps) => (
  <PageContext.Provider value={usePageProvider()}>
    {children}
  </PageContext.Provider>
)

const usePageProvider = () => {
  const [hideFooter, setHideFooter] = useState(false)
  const [header, setHeader] = useState(defaultHeaderProps)

  return { hideFooter, setHideFooter, header, setHeader }
}

export const usePage = () => useContext(PageContext)

export const usePageSettings = ({
  hideFooter = null,
  header = null,
}: {
  hideFooter?: boolean | null
  header?: Partial<PageHeaderProps> | null
} = {}) => {
  const navigationType = useNavigationType()

  const { setHideFooter, hideFooter: isFooterHidden, setHeader } = usePage()
  const headerReady = useRef(false)

  useEffect(() => {
    if (!hideFooter) return
    setHideFooter(hideFooter)

    return () => {
      if (isFooterHidden) setHideFooter(false)
    }
  }, [hideFooter, setHideFooter, isFooterHidden])

  useEffect(() => {
    if (!header) {
      setHeader(defaultHeaderProps)
      headerReady.current = false
      return
    }

    if (!headerReady.current && !!header) {
      const newHeaderConfig = {
        ...defaultHeaderProps,
        ...header,
      }
      setHeader(newHeaderConfig)
      headerReady.current = true
    }
  }, [header, setHeader, navigationType])
}
